import { Box } from '@chakra-ui/react';
import { uniqueId } from 'lodash';
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useViewer } from '../../../components/common/ForgeViewer';
import {
  Viewport,
  getPointInViewerCoordinateSystem,
  useSheetViewer,
} from '../../../components/common/SheetViewer';
import {
  useViewerMode,
  ViewerMode,
} from '../../../components/common/viewer-mode';
import {
  Coordinate2Result,
  CoordinateResult,
  OperationResult,
  Point2,
  ResultType,
} from '../../../domain/geometry/geometric-types';
import { useSheetShapes } from '../../../hooks/sheet-shapes';
import { toMultiPolygon2 } from '../../../domain/geometry/algorithms/util/type-mapping';
import { MeshResultVisualization } from './MeshResultVisualization';
import { Polygon2ResultVisualization } from './Polygon2ResultVisualization';
import { PolygonResultVisualization } from './PolygonResultVisualization';
import { Polyline2ResultVisualization } from './Polyline2ResultVisualization';
import { PolylineResultVisualization } from './PolylineResultVisualization';
import { CoordinateResultVisualization } from './CoordinateResultVisualization';
import { Coordinate2ResultVisualization } from './Coordinate2ResultVisualization';
import { Points2ResultVisualization } from './Points2ResultVisualization';
import { MultiPolygon2AlgorithmType } from 'src/domain/geometry/algorithm-types';
import { NumberWithPoints2Result } from 'src/domain/geometry/geometric-types';
import { useSheetViewerV2 } from 'src/components/viewers/SheetsViewerV2/hooks/useSheetViewerV2';
import { ApiCoordinatesConverter } from 'src/components/viewers/SheetsViewerV2/utils/apiCoordinatesConverter';

export function GeometricResultVisualization({
  operationResults,
}: {
  operationResults: OperationResult[];
}): ReactElement {
  const { viewerMode } = useViewerMode();
  switch (viewerMode) {
    case ViewerMode.Models:
      return (
        <ForgeGeometricResultVisualization
          operationResults={operationResults}
        />
      );
    case ViewerMode.Sheets:
      return (
        <SheetsGeometricResultVisualization
          operationResults={operationResults}
        />
      );
    case ViewerMode.SheetsV2:
      return (
        <SheetsV2GeometricResultVisualization
          operationResults={operationResults}
        />
      );
  }
}

function ForgeGeometricResultVisualization({
  operationResults,
}: {
  operationResults: OperationResult[];
}) {
  const overlayName = useMemo(() => {
    return uniqueId('geometric-result-visualization');
  }, []);

  const { viewer } = useViewer();

  useEffect(() => {
    if (!viewer) {
      return;
    }
    viewer.overlays.addScene(overlayName);
    return () => {
      viewer.overlays.removeScene(overlayName);
    };
  }, [overlayName, viewer]);

  if (!viewer) {
    return null;
  }

  return createPortal(
    <Box
      position="absolute"
      overflow="clip"
      w="100%"
      h="100%"
      zIndex={1}
      pointerEvents={'none'}
    >
      {operationResults.map((result) => (
        <VisualizedForgeResult
          key={`${result[1].algorithmType}-${result[1].modelUrn}-${result[1].dbId}`}
          overlayName={overlayName}
          operationResult={result}
          viewer={viewer}
        />
      ))}
    </Box>,
    viewer.clientContainer
  );
}

function VisualizedForgeResult({
  operationResult: [result, context],
  viewer,
  overlayName,
}: {
  viewer: Autodesk.Viewing.GuiViewer3D;
  operationResult: OperationResult;
  overlayName: string;
}) {
  if (!result) {
    return null;
  }
  switch (result.type) {
    case ResultType.MULTIPOLYLINE:
      return <PolylineResultVisualization result={result} viewer={viewer} />;
    case ResultType.MULTIPOLYGON:
      return (
        <PolygonResultVisualization
          result={result}
          overlayName={overlayName}
          viewer={viewer}
        />
      );
    case ResultType.MESH:
    case ResultType.TRIANGULATED_SURFACE:
      return (
        <MeshResultVisualization
          result={result}
          overlayName={overlayName}
          viewer={viewer}
        />
      );
    case ResultType.COORDINATE:
      if (
        'point' in result &&
        Array.isArray(result.point) &&
        result.point.length === 2
      ) {
        return (
          <Coordinate2ResultVisualization
            result={result as Coordinate2Result}
            getPointInDomCoordinateSystem={(p) => p}
          />
        );
      } else {
        return (
          <CoordinateResultVisualization
            result={result as CoordinateResult}
            viewer={viewer}
          />
        );
      }
    default:
      return null;
  }
}

function SheetsGeometricResultVisualization({
  operationResults,
}: {
  operationResults: OperationResult[];
}) {
  const {
    viewerRef,
    page,
    addViewportChangeHandler,
    removeViewportChangeHandler,
    shapesManager: { renderedSheetShapes },
    activeSheetId,
    pageNumber,
  } = useSheetViewer();

  const viewerContainer = viewerRef.current;

  const [viewerViewport, setViewerViewport] = useState<Viewport | null>(null);

  useEffect(() => {
    const onViewportChange = (vp: Viewport) => {
      setViewerViewport(vp);
    };
    addViewportChangeHandler(onViewportChange);
    return () => {
      removeViewportChangeHandler(onViewportChange);
    };
  }, [addViewportChangeHandler, removeViewportChangeHandler]);

  const _getPointInDomCoordinateSystem = useCallback(
    (pointInDom: Point2) => {
      if (!viewerViewport || !page || !viewerRef.current) {
        throw new Error('Viewer is not ready');
      }
      const viewerPoint = getPointInViewerCoordinateSystem(
        pointInDom,
        viewerViewport.offset,
        viewerViewport.scale,
        page
      );
      return [viewerPoint[0], viewerPoint[1]] as Point2;
    },
    [viewerViewport, page, viewerRef]
  );

  if (!viewerContainer || !viewerViewport || !page) {
    return null;
  }

  return createPortal(
    <Box overflow="clip" w="100%" h="100%" zIndex={1} pointerEvents={'none'}>
      {operationResults
        .filter(
          // Hide results that belong to a different page
          ([, context]) => {
            const shape = renderedSheetShapes[context.modelUrn]?.find(
              (shape) => shape.dbId === context.dbId
            );
            if (!shape) {
              return false;
            }
            return (
              shape.sheetId === activeSheetId &&
              shape.sheetPageNumber === pageNumber
            );
          }
        )
        .map((result) => (
          <VisualizedSheetsResult
            key={`${result[1].algorithmType}-${result[1].modelUrn}-${result[1].dbId}`}
            operationResult={result}
            getPointInDomCoordinateSystem={_getPointInDomCoordinateSystem}
          />
        ))}
    </Box>,
    viewerContainer
  );
}

// Check if result is a 2D coordinate result
function isCoordinate2Result(result: OperationResult[0]): boolean {
  return (
    result?.type === ResultType.COORDINATE &&
    'point' in result &&
    Array.isArray(result.point) &&
    result.point.length === 2
  );
}

// Check if result is a number result with points (type guard)
function isNumberWithPointsResult(
  result: OperationResult[0]
): result is NumberWithPoints2Result & { units: string } {
  return (
    result?.type === ResultType.NUMBER &&
    'points' in result &&
    Array.isArray((result as NumberWithPoints2Result).points)
  );
}

// Helper to render 2D sheet results based on result type
function render2DResult(
  result: OperationResult[0],
  context: OperationResult[1],
  getPointInDomCoordinateSystem: (point: Point2) => Point2,
  enclosingPolygon?: ReturnType<typeof toMultiPolygon2>
): React.ReactElement | null {
  if (!result) return null;

  if (result.type === ResultType.MULTIPOLYLINE2) {
    return (
      <Polyline2ResultVisualization
        result={result}
        getPointInDomCoordinateSystem={getPointInDomCoordinateSystem}
      />
    );
  }

  if (result.type === ResultType.MULTIPOLYGON2) {
    return (
      <Polygon2ResultVisualization
        result={result}
        getPointInDomCoordinateSystem={getPointInDomCoordinateSystem}
        enclosingPolygon={enclosingPolygon}
        algorithmType={context.algorithmType as MultiPolygon2AlgorithmType}
      />
    );
  }

  if (isCoordinate2Result(result)) {
    return (
      <Coordinate2ResultVisualization
        result={result as Coordinate2Result}
        getPointInDomCoordinateSystem={getPointInDomCoordinateSystem}
      />
    );
  }

  if (isNumberWithPointsResult(result)) {
    return (
      <Points2ResultVisualization
        points={result.points}
        getPointInDomCoordinateSystem={getPointInDomCoordinateSystem}
      />
    );
  }

  return null;
}

function VisualizedSheetsResult({
  operationResult: [result, context],
  getPointInDomCoordinateSystem,
}: {
  operationResult: OperationResult;
  getPointInDomCoordinateSystem: (pointInPdf: Point2) => Point2;
}) {
  const {
    shapes: { data: shapesData },
  } = useSheetShapes();

  const thisShape = shapesData?.find(
    (shape) => shape.dbId === context.dbId && shape.urn === context.modelUrn
  );

  const enclosingPolygon =
    context.algorithmType === 'Sheet shape inner area parameterized' &&
    thisShape?.sheetShapePolygon?.multipolygon
      ? toMultiPolygon2(thisShape?.sheetShapePolygon?.multipolygon)
      : undefined;

  return render2DResult(
    result,
    context,
    getPointInDomCoordinateSystem,
    enclosingPolygon
  );
}

function SheetsV2GeometricResultVisualization({
  operationResults,
}: {
  operationResults: OperationResult[];
}) {
  const {
    viewer: { aggregated },
    activeSheet,
    shapesManager: { renderedSheetShapes },
  } = useSheetViewerV2();

  const viewer = aggregated?.viewer;
  const activeSheetData = activeSheet?.data;
  // Use the viewer's container for the portal (same as Forge viewer)
  const viewerContainer = viewer?.container;

  // Track camera changes to force re-render when panning/zooming
  const [cameraVersion, setCameraVersion] = useState(0);

  useEffect(() => {
    if (!viewer) return;

    const onCameraChange = () => {
      setCameraVersion((v) => v + 1);
    };

    viewer.addEventListener(
      Autodesk.Viewing.CAMERA_CHANGE_EVENT,
      onCameraChange
    );

    return () => {
      viewer.removeEventListener(
        Autodesk.Viewing.CAMERA_CHANGE_EVENT,
        onCameraChange
      );
    };
  }, [viewer]);

  // Create coordinate converter
  const converter = useMemo(() => {
    if (!viewer || !activeSheetData?.viewport) return null;
    return new ApiCoordinatesConverter(viewer, activeSheetData.viewport);
  }, [viewer, activeSheetData?.viewport]);

  // Convert API point to screen coordinates
  // Include cameraVersion in dependencies to recalculate on pan/zoom
  const getPointInDomCoordinateSystem = useCallback(
    (pointInApi: Point2): Point2 => {
      if (!converter || !viewer) {
        throw new Error('Viewer is not ready');
      }
      // Convert from API coordinates to Autodesk world coordinates
      const worldPoint = converter.convertToAutodeskPoint({
        x: pointInApi[0],
        y: pointInApi[1],
      });
      // Project to screen coordinates
      const screenPoint = Autodesk.Viewing.MeasureCommon.project(
        worldPoint,
        viewer
      );
      return [screenPoint.x, screenPoint.y];
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [converter, viewer, cameraVersion]
  );

  // Filter results to only show those for the current active sheet
  const filteredResults = useMemo(() => {
    if (!activeSheetData) return [];

    return operationResults.filter(([, context]) => {
      const shapes = renderedSheetShapes[context.modelUrn];
      if (!shapes) return false;

      const shape = shapes.find((s) => s.dbId === context.dbId);
      if (!shape) return false;

      return (
        shape.sheetId === activeSheetData.id &&
        shape.sheetPageNumber === activeSheetData.page
      );
    });
  }, [operationResults, renderedSheetShapes, activeSheetData]);

  if (!viewerContainer || !converter || !viewer) {
    return null;
  }

  return createPortal(
    <Box
      position="absolute"
      top="0"
      left="0"
      overflow="clip"
      w="100%"
      h="100%"
      zIndex={1}
      pointerEvents={'none'}
    >
      {filteredResults.map((result) => (
        <VisualizedSheetsV2Result
          key={`${result[1].algorithmType}-${result[1].modelUrn}-${result[1].dbId}`}
          operationResult={result}
          getPointInDomCoordinateSystem={getPointInDomCoordinateSystem}
        />
      ))}
    </Box>,
    viewerContainer
  );
}

function VisualizedSheetsV2Result({
  operationResult: [result, context],
  getPointInDomCoordinateSystem,
}: {
  operationResult: OperationResult;
  getPointInDomCoordinateSystem: (pointInApi: Point2) => Point2;
}) {
  const {
    shapesManager: { renderedSheetShapes },
  } = useSheetViewerV2();

  // Find the shape to get enclosing polygon for fire sectioning
  const allShapes = Object.values(renderedSheetShapes).flat();
  const thisShape = allShapes.find(
    (shape) => shape.dbId === context.dbId && shape.urn === context.modelUrn
  );

  const enclosingPolygon =
    context.algorithmType === 'Sheet shape inner area parameterized' &&
    thisShape?.sheetShapePolygon?.multipolygon
      ? toMultiPolygon2(thisShape?.sheetShapePolygon?.multipolygon)
      : undefined;

  return render2DResult(
    result,
    context,
    getPointInDomCoordinateSystem,
    enclosingPolygon
  );
}
